/* eslint-disable react-hooks/exhaustive-deps */
import { useEventStream } from '@/Components/Hooks/useEventStream';
import {
  AUTO_REFRESH_DELAY,
  DEVICE_ALERTS_EVENTS,
  HUB_DASHBOARD_QUERY_PARAM,
} from '@/constants/device';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { getProfilePics, getUserHubs } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import {
  getProfilePicUrl,
  objectSort,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Actions, PhotoResolution } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { isNull, values } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useUserHubs from '../Hooks/useUserHubs';
import HubDetails from '../HubDetails/HubDetails';
import UserHubsComponent from './UserHubs.component';

const UserHubs = () => {
  const queryClient = useQueryClient();
  const currentUser = useSelector(getCurrentUser);
  const useruuid = useMemo(() => currentUser.useruuid, [currentUser]);
  const userData = useSelector((user) => user.data.userInfo);
  const updatedUserInfo = userData[useruuid];
  const [action, setAction] = useState(null);
  const interval = useMemo(() => (isNull(action) ? AUTO_REFRESH_DELAY : false), [action]);
  const { refetch, isFetching, isLoading } = useUserHubs({
    useruuid,
    refetchInterval: interval,
  });
  const userHubs = useSelector(getUserHubs(useruuid));
  const resourceMap = useSelector(getProfilePics());
  const mUserHubs = useMemo(
    () =>
      userHubs.map((hub) => ({
        ...hub,
        profilePic64: getProfilePicUrl(hub.useruuid, resourceMap, PhotoResolution.R64),
      })),
    [userHubs, resourceMap]
  );
  const mOrderedUserHubs = useMemo(() => {
    return [...mUserHubs].sort(objectSort('firstname'));
  }, [mUserHubs]);
  const [orderedUserHubs, setOrderedUserHubs] = useState(mOrderedUserHubs);
  const [record, setRecord] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const principalUserUuid = searchParams.get(HUB_DASHBOARD_QUERY_PARAM);
  const userHubCall = true;
  const sendMsgDisplay = true;

  const { latestEvent: sseEvent } = useEventStream(
    { useruuid: currentUser?.useruuid },
    values(DEVICE_ALERTS_EVENTS),
    userHubs.map((hub) => hub.useruuid),
    { enabled: isNull(action) }
  );

  const normalizeAlertType = (type = '') => type.replace(/__triggers$/, '');

  const getEventReceivedTime = (event) => {
    const timestampMs = event?.utc_timestamp_inms || event?.recv_time;
    if (timestampMs) {
      return new Date(timestampMs).toISOString();
    }
    return new Date().toISOString();
  };

  useEffect(() => {
    setOrderedUserHubs(mOrderedUserHubs);
  }, [mOrderedUserHubs]);

  useEffect(() => {
    if (!sseEvent?.message_type || !sseEvent?.useruuid) return;

    const eventAlertType = normalizeAlertType(sseEvent.message_type);
    const eventTime = getEventReceivedTime(sseEvent);

    setOrderedUserHubs((prevHubs) => {
      let hasUpdatedHub = false;

      const updatedHubs = prevHubs.map((hub) => {
        if (hub.useruuid !== sseEvent.useruuid) {
          return hub;
        }

        hasUpdatedHub = true;
        const existingAlerts = Array.isArray(hub.alerts) ? hub.alerts : [];
        const alertIndex = existingAlerts.findIndex(
          (alert) => normalizeAlertType(alert?.alert) === eventAlertType
        );

        if (alertIndex >= 0) {
          const updatedAlerts = [...existingAlerts];
          updatedAlerts[alertIndex] = {
            ...updatedAlerts[alertIndex],
            alert: eventAlertType,
            last_receivedtime_utc: eventTime,
          };
          return { ...hub, alerts: updatedAlerts };
        }

        return {
          ...hub,
          alerts: [...existingAlerts, { alert: eventAlertType, last_receivedtime_utc: eventTime }],
        };
      });

      return hasUpdatedHub ? updatedHubs : prevHubs;
    });
  }, [sseEvent]);

  /* handler functions */
  const onActionClick = useCallback(
    (action, record, openSubMenu, refresh = false) =>
      () => {
        setAction(action);
        setRecord(record);
        setOpenSubMenu(openSubMenu);
        refresh && refetch();
      },
    [refetch]
  );

  useEffect(() => {
    if (record) {
      const newRecord = userHubs.find((a) => a.hubid === record.hubid);
      setRecord(newRecord);
    }
    if (userHubs?.length === 1 && currentUser?.permission_level !== 'Staff Admin') {
      onActionClick(Actions.hubDetails, userHubs[0], true)();
    }
  }, [userHubs, currentUser]);

  /* Wait for the hubs before resolving the account, then always strip the param so it can't
     leak onto other routes via the sidebar's search param passthrough. */
  useEffect(() => {
    if (!principalUserUuid || isLoading) return;

    const hub = userHubs.find(({ useruuid }) => useruuid === principalUserUuid);
    if (hub) {
      onActionClick(Actions.hubDetails, hub, true)();
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(HUB_DASHBOARD_QUERY_PARAM);
    setSearchParams(nextSearchParams, { replace: true });
  }, [principalUserUuid, userHubs, isLoading, searchParams]);

  useEffect(() => {
    if (isNull(action)) {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          ['getUserEventsTrend', 'getUserAccessories', 'getMergedConfig', 'getContacts'].includes(
            queryKey?.[0]
          ),
      });
    }
  }, [action, queryClient]);

  const visibleUserHubs = orderedUserHubs.length ? orderedUserHubs : mOrderedUserHubs;

  return !action ? (
    <UserHubsComponent
      userHubs={visibleUserHubs}
      fetchingUserHubs={isLoading}
      isBackgroundFetching={isFetching}
      onActionClick={onActionClick}
    />
  ) : action === Actions.hubDetails ? (
    /* keyed so switching accounts in place (e.g. from an alert notification) resets HubDetails */
    <HubDetails
      key={record?.useruuid}
      hubRecord={record}
      fetchUserHubs={refetch}
      openSubMenu={openSubMenu}
      userHubsData={mUserHubs}
      onActionClick={onActionClick}
      sendMsgDisplay={sendMsgDisplay}
      loginUserTimeformat={{ is24hoursformat: updatedUserInfo?.is24hoursformat }}
      onUserHubActionClick={onActionClick}
      loginUserMeasurementUnit={{ measurement: updatedUserInfo?.measurement }}
    />
  ) : action === Actions.listContacts ? (
    <HubDetails
      activeSubMenu={'contacts'}
      hubRecord={record}
      openSubMenu={openSubMenu}
      userHubCall={userHubCall}
      onActionClick={onActionClick}
      onUserHubActionClick={onActionClick}
      sendMsgDisplay={sendMsgDisplay}
      userHubsData={mUserHubs}
    />
  ) : action === Actions.inviteContact ? (
    <HubDetails
      activeSubMenu={'inviteContact'}
      action={action}
      hubRecord={record}
      openSubMenu={openSubMenu}
      userHubCall={userHubCall}
      onActionClick={onActionClick}
      sendMsgDisplay={sendMsgDisplay}
      onUserHubActionClick={onActionClick}
    />
  ) : action === Actions.addEvent ? (
    <HubDetails
      activeSubMenu={'schedule'}
      activeSubMenuContent={'addEvent'}
      action={action}
      hubRecord={record}
      userHubsData={mUserHubs}
      openSubMenu={true}
      userHubCall={userHubCall}
      onActionClick={onActionClick}
      sendMsgDisplay={sendMsgDisplay}
      onUserHubActionClick={onActionClick}
      loginUserTimeformat={{ is24hoursformat: updatedUserInfo?.is24hoursformat }}
    />
  ) : action === Actions.assignStaff ? (
    <HubDetails
      activeSubMenu={'assignStaff'}
      hubRecord={record}
      openSubMenu={openSubMenu}
      userHubCall={userHubCall}
      onActionClick={onActionClick}
      sendMsgDisplay={sendMsgDisplay}
      onUserHubActionClick={onActionClick}
    />
  ) : action === Actions.sendMessage ? (
    <HubDetails
      activeSubMenu={'sendMessage'}
      hubRecord={record}
      openSubMenu={openSubMenu}
      userHubCall={userHubCall}
      onActionClick={onActionClick}
      sendMsgDisplay={false}
      onUserHubActionClick={onActionClick}
    />
  ) : null;
};

UserHubs.propTypes = {};

export default UserHubs;
